import { Injectable } from '@angular/core';

/**
 * Provides caching
 * Cache-data ist stored in sessionStorage of the client
 *
 * @export
 * @class CacheService
 */
@Injectable({
  providedIn: 'root'
})
export class CacheService {

  /**
   * prefix for cache user key
   *
   * @private
   * @static
   * @memberof CacheService
   */
  private static readonly _keyPrefix = 'cache_';

  /**
   * Get item from cache or create new and add to cache
   *
   * @template T
   * @param {string} key
   * @param {() => T} creator
   * @returns {T}
   * @memberof CacheService
   */
  GetOrAdd<T>(key: string, creator: () => T): T {
    const cacheKey = this._getKey(key);
    if (!this._contains(cacheKey)) {
      this._set(cacheKey, creator());
    }
    return this._get(cacheKey);
  }

  /**
   * Add new item to cache
   * throws error if item already exists
   *
   * @template T
   * @param {string} key
   * @param {T} value
   * @memberof CacheService
   */
  Add<T>(key: string, value: T): void {
    const cacheKey = this._getKey(key);
    if (this._contains(cacheKey)) {
      throw new Error(`an element with the key ${key} already exist in cache`);
    }
    this._set(cacheKey, value);
  }

  /**
   * Get existing item from cache
   * returns null if item does not exist
   *
   * @template T
   * @param {string} key
   * @returns {T}
   * @memberof CacheService
   */
  Get<T>(key: string): T {
    return this._get(this._getKey(key));
  }

  /**
   * Set value of an item in cache
   * overwrites existing values
   *
   * @template T
   * @param {string} key
   * @param {T} value
   * @memberof CacheService
   */
  Set<T>(key: string, value: T): void {
    this._set(this._getKey(key), value);
  }

  /**
   * Remove item with given key
   *
   * @param {string} key
   * @memberof CacheService
   */
  Delete(key: string): void {
    this._set(this._getKey(key), null);
  }

  /**
   * Clears all entries starting with keyStart
   *
   * @param {string} keyStart
   * @memberof CacheService
   */
  ClearSection(keyStart: string): void {
    this._clearSection(this._getKey(keyStart));
  }

  /**
   * Clear the cache
   *
   * @memberof CacheService
   */
  Clear(): void {
    this._clear();
  }

  /**
   * Returns true if an item with given key exists in cache
   *
   * @param {string} key
   * @returns {boolean}
   * @memberof CacheService
   */
  Contains(key: string): boolean {
    return this._contains(this._getKey(key));
  }

  /**
   * Creates a prefixed key to use in sessionStorage
   *
   * @private
   * @param {string} key
   * @returns {CacheKey}
   * @memberof CacheService
   */
  private _getKey(key: string): CacheKey {
    return new CacheKey(key, CacheService._keyPrefix);
  }

  private _clear(): void {
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key.startsWith(CacheService._keyPrefix)) {
        sessionStorage.removeItem(key);
      }
    }
  }

  _clearSection(keyStart: CacheKey): void {
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key.startsWith(keyStart.value)) {
        sessionStorage.removeItem(key);
      }
    }
  }

  private _contains(key: CacheKey): boolean {
    return window.sessionStorage.getItem(key.value) !== null;
  }

  private _get<T>(key: CacheKey): T {
    return <T>JSON.parse(window.sessionStorage.getItem(key.value));
  }

  private _set<T>(key: CacheKey, value: T): void {
    if (value === null) {
      window.sessionStorage.removeItem(key.value);
    } else {
      window.sessionStorage.setItem(key.value, JSON.stringify(value));
    }
  }

}

/**
 * Object to ensure that cache-key has been prefixed
 *
 * @class CacheKey
 */
class CacheKey {

  /**
   * Actual value to use as key in sessionStorage
   *
   * @type {string}
   * @memberof CacheKey
   */
  readonly value: string;

  constructor(key: string, prefix: string) {
    this.value = prefix + key.replace(' ', '_');
  }

}
